import { useEffect, useState } from 'react';
import { VoiceCallRecordingDto } from '@act/shared/data-transfer-objects';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { transcriptFormatter } from 'utilities/formatters/transcript-formatter';
import BubbleChat from './BubbleTranscript';
import PlainTextChat from './PlainTextTranscript';
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import PostCallFeedback from './post-call-feedback/PostCallFeedback';
import PaddedPane from './PaddedPane';
import { TrackingProvider, useTracking } from 'react-components/tracking';
import { NO_TRANSCRIPTION_MESSAGE } from './transcription.utils';

interface TranscriptionPaneProps {
  voiceCallRecording: VoiceCallRecordingDto;
}

const TranscriptionPane = ({ voiceCallRecording }: TranscriptionPaneProps) => {
  const [viewMode, setViewMode] = useState<'speech-bubble' | 'plain-text'>(
    'speech-bubble'
  );

  const [isFeedbackOpen, setIsFeedbackOpen] = useState(
    voiceCallRecording?.isAccurateTranscription === null
  );

  const { generateTrackingProps } = useTracking();

  function changeViewMode(mode: 'speech-bubble' | 'plain-text') {
    if (mode) {
      setViewMode(mode);
    }
  }

  useEffect(() => {
    if (voiceCallRecording?.isAccurateTranscription === null) {
      setIsFeedbackOpen(true);
    }
  }, [voiceCallRecording?.isAccurateTranscription]);

  const recordingTranscription =
    voiceCallRecording?.recordingTranscription ?? '';
  const transcriptLines =
    recordingTranscription === NO_TRANSCRIPTION_MESSAGE
      ? [NO_TRANSCRIPTION_MESSAGE]
      : transcriptFormatter.transcriptSplitter(recordingTranscription);

  if (!voiceCallRecording) {
    return null;
  }

  return (
    <TrackingProvider contextName="transcription-pane">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
        }}
      >
        <PaddedPane hasTopMargin>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            sx={{ marginLeft: 'auto' }}
            onChange={(_, value) => changeViewMode(value)}
          >
            <ToggleButton
              value="speech-bubble"
              defaultChecked
              {...generateTrackingProps('speech-bubble-view-button')}
            >
              <MessageOutlinedIcon />
            </ToggleButton>
            <ToggleButton
              value="plain-text"
              {...generateTrackingProps('plain-text-view-button')}
            >
              <NotesOutlinedIcon />
            </ToggleButton>
          </ToggleButtonGroup>

          <Box
            sx={{
              overflow: 'auto',
              flex: 1,
              paddingBottom: '20px',
              paddingRight: '20px',
              minHeight: 0,
            }}
          >
            {viewMode === 'speech-bubble' && (
              <BubbleChat transcriptLines={transcriptLines} />
            )}
            {viewMode === 'plain-text' && (
              <PlainTextChat transcriptLines={transcriptLines} />
            )}
          </Box>
        </PaddedPane>

        {isFeedbackOpen && (
          <Box sx={{ flexShrink: 0 }}>
            <PostCallFeedback
              feedbackType="transcription"
              voiceCallRecording={voiceCallRecording}
              onClose={() => setIsFeedbackOpen(false)}
            />
          </Box>
        )}
      </Box>
    </TrackingProvider>
  );
};

export default TranscriptionPane;
